/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _set from "lodash/set";

import historyCompanyDashboardDataService from "services/historyCompanyDashboard";
import { getShareAuthorizationsAsync } from "./shareCapitalAuthorization";

export const getHistoryCompanyOfficialsAsync = createAsyncThunk(
    "historyCompanyDashboard/getHistoryCompanyOfficialsAsync",
    async ({ companyId, queryParams }, thunkAPI) => {
        try {
            const res = await historyCompanyDashboardDataService.getHistoryCompanyOfficials(
                companyId,
                queryParams
            );
            return res.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const getHistoryCompanyBeneficialOwnersAsync = createAsyncThunk(
    "historyCompanyDashboard/getHistoryCompanyBeneficialOwnersAsync",
    async ({ companyId }, thunkAPI) => {
        try {
            const res = await historyCompanyDashboardDataService.getHistoryCompanyBeneficialOwners(
                companyId
            );
            return res.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const getHistoryCompanyDashboardAsync = createAsyncThunk(
    "historyCompanyDashboard/getHistoryCompanyDashboardAsync",
    async ({ companyId }, thunkAPI) => {
        try {
            const res = await historyCompanyDashboardDataService.getHistoryCompany(companyId);
            thunkAPI.dispatch(getShareAuthorizationsAsync(companyId));
            thunkAPI.dispatch(
                getHistoryCompanyOfficialsAsync({
                    companyId,
                    queryParams: { isDashboard: true },
                })
            );
            thunkAPI.dispatch(getHistoryCompanyBeneficialOwnersAsync({ companyId }));
            return res.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

export const getOfficial = createAsyncThunk(
    "historyCompanyDashboard/getOfficial",
    async ({ companyId, officialId, idType, JSGroupID }, thunkAPI) => {
        try {
            const res = await historyCompanyDashboardDataService.getOfficial(
                companyId,
                officialId,
                idType,
                JSGroupID
            );
            if (res) {
                return res.data;
            }
        } catch (error) {
            return thunkAPI.rejectWithValue(error);
        }
    }
);

const initialState = {
    company: null,
    manageCompanyTransactions: {
        items: [],
        totalCount: 0,
    },
    currentUserRow: null,
    isCurrentUserCorporateRow: null,
    companyOfficials: {
        items: [],
        totalCount: 0,
    },
    beneficialOwners: {
        items: [],
        totalCount: 0,
    },
    groupedBeneficialOwners: {
        items: [],
        totalCount: 0,
    },
    dashboardRoles: [],
    dashboardPermissions: [],
    designation: "",
    breadCrumbs: [],
    official: null,
    transactionCompany: {},
    getCompanyStatus: {
        status: "idle",
        error: false,
    },
    getCompanyOfficialsStatus: {
        status: "loading",
        error: false,
    },
    getManageCompanyTransactionsStatus: {
        status: "loading",
        error: false,
    },
    getCompanyBeneficialOwnersStatus: {
        status: "idle",
        error: false,
    },
    getOfficialStatus: {
        status: "idle",
        error: false,
    },
    getTransactionCompanyStatus: {
        status: "idle",
        error: false,
    },
};

const historyCompanyDashboardSlice = createSlice({
    name: "historyCompanyDashboard",
    initialState,
    reducers: {
        updateHistoryCompanyDashboardState: (state, action) => {
            state.historyCompany = { ...state.historyCompany, ...action.payload };
        },
        updateOfficialState: (state, action) => {
            state.official = { ...state.official, ...action.payload };
        },
        updateManageHistoryCompanyTransactionsNestedField: (
            state,
            { payload: { newNestedField, updateEffectPath } }
        ) => {
            return _set(state, updateEffectPath.split(".").slice(1), newNestedField);
        },
        updateOfficialNestedField: (
            state,
            { payload: { newNestedField, updateEffectPath } }
        ) => {
            return _set(state, updateEffectPath.split(".").slice(1), newNestedField);
        },
        resetOfficial: (state) => {
            state.official = null;
            state.getOfficialStatus = {
                status: "idle",
                error: false,
            };
        },
        resetTransactionHistoryCompany: (state) => {
            state.transactionHistoryCompany = {};
        },
        resetHistoryCompanyDashboard: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(getHistoryCompanyDashboardAsync.pending, (state) => {
                state.getCompanyStatus.status = "loading";
                state.getCompanyStatus.error = false;
            })
            .addCase(getHistoryCompanyDashboardAsync.fulfilled, (state, action) => {
                state.getCompanyStatus.status = "idle";
                state.getCompanyStatus.error = false;
                state.company = action.payload.company;
                state.dashboardRoles = action.payload.dashboardRoles;
                state.dashboardPermissions = action.payload.dashboardPermissions ?? [];
                state.currentUserRow = action.payload.currentUserRow;
                state.isCurrentUserCorporateRow =
                    action.payload.isCurrentUserCorporateRow;
                state.breadCrumbs = action.payload.breadCrumbs ?? [];
                state.designation = action.payload?.designation ?? "";
            })
            .addCase(getHistoryCompanyDashboardAsync.rejected, (state, action) => {
                state.getCompanyStatus.status = "failed";
                state.getCompanyStatus.error = action.payload.messageText;
            })
            .addCase(getHistoryCompanyOfficialsAsync.pending, (state) => {
                state.getCompanyOfficialsStatus.status = "loading";
                state.getCompanyOfficialsStatus.error = false;
            })
            .addCase(getHistoryCompanyOfficialsAsync.fulfilled, (state, action) => {
                state.getCompanyOfficialsStatus.status = "idle";
                state.getCompanyOfficialsStatus.error = false;
                state.companyOfficials = action.payload;
            })
            .addCase(getHistoryCompanyOfficialsAsync.rejected, (state, action) => {
                state.getCompanyOfficialsStatus.status = "failed";
                state.getCompanyOfficialsStatus.error = action.payload.messageText;
            })
            .addCase(getHistoryCompanyBeneficialOwnersAsync.pending, (state) => {
                state.getCompanyBeneficialOwnersStatus.status = "loading";
                state.getCompanyBeneficialOwnersStatus.error = false;
            })
            .addCase(getHistoryCompanyBeneficialOwnersAsync.fulfilled, (state, action) => {
                state.getCompanyBeneficialOwnersStatus.status = "idle";
                state.getCompanyBeneficialOwnersStatus.error = false;
                state.beneficialOwners = action.payload.beneficialOwners;
                state.groupedBeneficialOwners = action.payload.groupedBeneficialOwners;
            })
            .addCase(getHistoryCompanyBeneficialOwnersAsync.rejected, (state, action) => {
                state.getCompanyBeneficialOwnersStatus.status = "failed";
                state.getCompanyBeneficialOwnersStatus.error =
                    action.payload.messageText;
            })
            .addCase(getOfficial.pending, (state) => {
                state.getOfficialStatus.status = "loading";
                state.getOfficialStatus.error = false;
            })
            .addCase(getOfficial.fulfilled, (state, action) => {
                state.getOfficialStatus.status = "idle";
                state.getOfficialStatus.error = false;
                state.official = { ...state.official, ...action.payload };
            })
            .addCase(getOfficial.rejected, (state, action) => {
                state.getOfficialStatus.status = "failed";
                state.getOfficialStatus.error = action.payload.messageText;
            })

    },
});

export const {
    updateHistoryCompanyDashboardState,
    updateOfficialState,
    updateOfficialNestedField,
    resetOfficial,
    resetHistoryCompanyDashboard,
    resetTransactionHistoryCompany,
    updateManageHistoryCompanyTransactionsNestedField,
} = historyCompanyDashboardSlice.actions;

const { reducer } = historyCompanyDashboardSlice;

export default reducer;
